import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Repository } from '../types';
import apiService from '../services/api';
import '../styles/ProjectAnalysisPage.css';

interface AnalysisStep {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed';
}

interface AnalysisContent {
  projectOverview: string;
  coreFeatures: string;
  quickStart: string;
  techArchitecture: string;
  deploymentGuide: string;
  codeAnalysis: string;
  communityActivity: string;
  comparison: string;
  riskWarning: string;
}

const ProjectAnalysisPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { owner, repo } = useParams<{ owner: string; repo: string }>();

  // 优先使用state中的项目数据，如果没有则根据URL参数获取
  const [project, setProject] = useState<Repository | null>(location.state?.project || null);

  const [currentStep, setCurrentStep] = useState(0);
  const [analysisContent, setAnalysisContent] = useState<AnalysisContent | null>(null);
  const [activeSection, setActiveSection] = useState('projectOverview');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [readmeImages, setReadmeImages] = useState<string[]>([]);
  const [analysisTime, setAnalysisTime] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const steps: AnalysisStep[] = [
    { id: 'fetch', name: '获取GitHub材料', status: 'pending' },
    { id: 'organize', name: '整理数据', status: 'pending' },
    { id: 'analyze', name: 'LLM解析', status: 'pending' },
    { id: 'present', name: '结果呈现', status: 'pending' }
  ];

  const navigationItems = [
    { id: 'projectOverview', name: '项目概述', icon: 'fab fa-github' },
    { id: 'coreFeatures', name: '核心功能分析', icon: 'fas fa-star' },
    { id: 'quickStart', name: '快速上手指南', icon: 'fas fa-play' },
    { id: 'techArchitecture', name: '技术架构分析', icon: 'fas fa-code-branch' },
    { id: 'deploymentGuide', name: '部署实施指南', icon: 'fas fa-cloud' },
    { id: 'codeAnalysis', name: '代码结构解析', icon: 'fas fa-file-code' },
    { id: 'communityActivity', name: '社区活跃动态', icon: 'fas fa-comments' },
    { id: 'comparison', name: '同类项目分析', icon: 'fas fa-git-alt' },
    { id: 'riskWarning', name: '风险评估提示', icon: 'fas fa-shield-alt' }
  ];

  // 根据URL参数获取项目信息
  const fetchProjectFromParams = async () => {
    if (!owner || !repo) {
      navigate('/search');
      return;
    }

    try {
      // 调用API获取项目信息
      const projectData = await apiService.getRepositoryInfo(`${owner}/${repo}`);
      if (projectData) {
        setProject(projectData);
      } else {
        navigate('/search');
      }
    } catch (error) {
      console.error('Failed to fetch project:', error);
      navigate('/search');
    }
  };

  useEffect(() => {
    if (!project && owner && repo) {
      // 如果没有项目数据但有URL参数，则获取项目信息
      fetchProjectFromParams();
    } else if (!project && (!owner || !repo)) {
      // 如果既没有项目数据也没有URL参数，则跳转到搜索页
      navigate('/search');
      return;
    }

    if (project) {
      // 检查是否有缓存数据
      const cachedAnalysis = location.state?.cachedAnalysis;
      const isFromCache = location.state?.fromCache;

      if (cachedAnalysis && isFromCache) {
        // 使用缓存数据
        setFromCache(true);
        setAnalysisTime(cachedAnalysis.created_at || cachedAnalysis.timestamp);
        if (cachedAnalysis.analysis) {
          setAnalysisContent(parseAnalysisContent(cachedAnalysis.analysis));
        }
        setCurrentStep(steps.length); // 直接设置为完成状态
        setIsAnalyzing(false);
      } else {
        // 正常分析流程
        setFromCache(false);
        startAnalysis();
      }

      fetchReadmeImages();
    }
  }, [project, owner, repo, navigate]);

  // 获取README中的图片
  const fetchReadmeImages = async () => {
    try {
      const response = await fetch(`https://api.github.com/repos/${project?.full_name}/readme`);
      if (response.ok) {
        const data = await response.json();
        const content = atob(data.content);
        console.log('README content preview:', content.substring(0, 500));

        // 更全面的图片匹配正则表达式，按顺序匹配
        const allMatches: Array<{url: string, index: number}> = [];

        // 1. 匹配HTML格式的图片：<img src="url">
        const htmlImgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
        let match;
        while ((match = htmlImgRegex.exec(content)) !== null) {
          allMatches.push({url: match[1], index: match.index});
        }

        // 2. 匹配链接包裹的图片：[![alt](url)](link)
        const linkWrappedRegex = /\[!\[.*?\]\((.*?)\)\]\(.*?\)/g;
        while ((match = linkWrappedRegex.exec(content)) !== null) {
          allMatches.push({url: match[1], index: match.index});
        }

        // 3. 匹配标准图片：![alt](url)
        const standardRegex = /!\[.*?\]\((.*?)\)/g;
        while ((match = standardRegex.exec(content)) !== null) {
          allMatches.push({url: match[1], index: match.index});
        }

        // 按在文档中出现的位置排序
        allMatches.sort((a, b) => a.index - b.index);

        const images: string[] = [];

        for (const item of allMatches) {
          let imageUrl = item.url;
          console.log('Found image at position', item.index, ':', imageUrl);

          // 跳过徽章图片（通常是 shields.io 或其他徽章服务）
          if (imageUrl.includes('shields.io') ||
              imageUrl.includes('badge') ||
              imageUrl.includes('img.shields.io') ||
              imageUrl.includes('trendshift.io')) {
            console.log('Skipping badge:', imageUrl);
            continue;
          }

          // 处理相对路径
          if (!imageUrl.startsWith('http')) {
            imageUrl = `https://raw.githubusercontent.com/${project?.full_name}/main/${imageUrl}`;
            console.log('Converted to:', imageUrl);
          }

          // 避免重复添加
          if (!images.includes(imageUrl)) {
            images.push(imageUrl);
          }
        }

        console.log('All extracted images:', images);
        console.log('Selected images (first 3):', images.slice(0, 3));
        setReadmeImages(images.slice(0, 3)); // 最多显示3张图片
      }
    } catch (error) {
      console.error('Failed to fetch README images:', error);
    }
  };

  // 复制代码到剪贴板
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // 可以添加一个toast提示
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  // 简单的语法高亮函数
  const highlightCode = (code: string, language: string = '') => {
    // 智能缩进修复 - 基于内容结构自动调整
    const lines = code.replace(/\t/g, '  ').split('\n');
    const processedLines: string[] = [];
    let currentIndentLevel = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      if (!trimmed) {
        processedLines.push('');
        continue;
      }

      // YAML/配置文件格式检测
      if (trimmed.includes(':')) {
        if (trimmed.startsWith('-')) {
          // 列表项，保持当前缩进级别
          processedLines.push('  '.repeat(Math.max(0, currentIndentLevel)) + trimmed);
        } else if (trimmed.match(/^[a-zA-Z][a-zA-Z0-9_]*:/)) {
          // 顶级键，重置缩进
          currentIndentLevel = 0;
          processedLines.push(trimmed);
        } else {
          // 嵌套键，增加缩进
          currentIndentLevel = 1;
          processedLines.push('  ' + trimmed);
        }
      } else {
        // 非键值对内容，保持当前缩进
        processedLines.push('  '.repeat(Math.max(0, currentIndentLevel)) + trimmed);
      }
    }

    let highlighted = processedLines.join('\n');

    // Python/JavaScript 关键字
    const keywords = ['import', 'from', 'def', 'class', 'if', 'else', 'elif', 'for', 'while', 'try', 'except', 'finally', 'with', 'as', 'return', 'yield', 'break', 'continue', 'pass', 'lambda', 'and', 'or', 'not', 'in', 'is', 'True', 'False', 'None', 'function', 'var', 'let', 'const', 'async', 'await', 'export', 'default'];

    // Docker/Shell 关键字
    const dockerKeywords = ['FROM', 'RUN', 'COPY', 'ADD', 'WORKDIR', 'EXPOSE', 'CMD', 'ENTRYPOINT', 'ENV', 'ARG', 'VOLUME', 'USER', 'LABEL'];

    // 合并关键字
    const allKeywords = [...keywords, ...dockerKeywords];

    // 高亮关键字
    allKeywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'g');
      highlighted = highlighted.replace(regex, `<span class="keyword">${keyword}</span>`);
    });

    // 高亮字符串 (单引号和双引号)
    highlighted = highlighted.replace(/(["'])((?:(?!\1)[^\\]|\\.)*)(\1)/g, '<span class="string">$1$2$3</span>');

    // 高亮注释
    highlighted = highlighted.replace(/(#.*$)/gm, '<span class="comment">$1</span>');

    // 高亮数字
    highlighted = highlighted.replace(/\b(\d+\.?\d*)\b/g, '<span class="number">$1</span>');

    // 高亮函数调用 (word followed by parentheses)
    highlighted = highlighted.replace(/\b([a-zA-Z_][a-zA-Z0-9_]*)\s*(?=\()/g, '<span class="function">$1</span>');

    // 高亮模块名 (after import/from)
    highlighted = highlighted.replace(/(import|from)\s+([a-zA-Z_][a-zA-Z0-9_\.]*)/g, '$1 <span class="module">$2</span>');

    return highlighted;
  };

  // 渲染代码块
  const renderCodeBlock = (code: string, language: string = '') => {
    const highlightedCode = highlightCode(code, language);

    return (
      <div className="code-block" key={Math.random()}>
        <button
          className="copy-btn"
          onClick={() => copyToClipboard(code)}
          title="复制代码"
        >
          <i className="fas fa-copy"></i> 复制
        </button>
        <pre>
          <code
            className={`language-${language}`}
            dangerouslySetInnerHTML={{ __html: highlightedCode }}
          />
        </pre>
      </div>
    );
  };

  // 渲染分析内容，处理代码块和格式
  const renderAnalysisContent = (content: string, sectionId: string) => {
    if (!content) return content;

    // 处理代码块
    const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = codeBlockRegex.exec(content)) !== null) {
      // 添加代码块前的文本
      if (match.index > lastIndex) {
        parts.push(
          <div key={`text-${lastIndex}`} className="text-content">
            {formatText(content.slice(lastIndex, match.index), sectionId)}
          </div>
        );
      }

      // 添加代码块
      const language = match[1] || '';
      const code = match[2].trim();
      parts.push(renderCodeBlock(code, language));

      lastIndex = match.index + match[0].length;
    }

    // 添加剩余文本
    if (lastIndex < content.length) {
      parts.push(
        <div key={`text-${lastIndex}`} className="text-content">
          {formatText(content.slice(lastIndex), sectionId)}
        </div>
      );
    }

    return parts.length > 0 ? <div className="formatted-content">{parts}</div> : formatText(content, sectionId);
  };

  // 格式化文本，处理标题、列表等
  const formatText = (text: string, sectionId?: string) => {
    return text.split('\n').map((line, index) => {
      // 处理标题
      if (line.startsWith('### ')) {
        return <h4 key={index} className="sub-title">{line.slice(4)}</h4>;
      }
      if (line.startsWith('## ')) {
        return <h3 key={index} className="section-subtitle">{line.slice(3)}</h3>;
      }
      if (line.startsWith('# ')) {
        return <h2 key={index} className="main-title">{line.slice(2)}</h2>;
      }

      // 处理特定标题加粗
      const titleKeywords = ['项目概述', '技术特点', '应用场景', '发展潜力', '学习价值'];
      const trimmedLine = line.trim();

      // 检查是否匹配标题（支持带图标和不带图标的版本）
      const isTitle = titleKeywords.some(keyword => {
        return trimmedLine === keyword ||
               trimmedLine.startsWith(keyword + '：') ||
               trimmedLine.startsWith(keyword + ':') ||
               // 更精确的匹配：只匹配以图标开头的标题或独立的关键词
               (trimmedLine.startsWith('🎯') && trimmedLine.includes(keyword)) ||
               (trimmedLine.startsWith('💼') && trimmedLine.includes(keyword)) ||
               (trimmedLine.startsWith('📈') && trimmedLine.includes(keyword)) ||
               (trimmedLine.startsWith('🎓') && trimmedLine.includes(keyword)) ||
               // 或者是独立成行的关键词（长度较短，不是长段落）
               (trimmedLine === keyword || (trimmedLine.length < 20 && trimmedLine.includes(keyword) && !trimmedLine.includes('。')));
      });

      if (isTitle) {
        // 在项目概述部分，跳过"项目概述"标题的显示
        if (sectionId === 'projectOverview' && (trimmedLine === '项目概述' || trimmedLine.startsWith('项目概述：') || trimmedLine.startsWith('项目概述:'))) {
          return null;
        }

        // 将标题转换为带图标的版本
        let displayTitle = trimmedLine;
        let titleClassName = "analysis-section-title";

        if (trimmedLine.includes('技术特点') && !trimmedLine.includes('🎯')) {
          displayTitle = trimmedLine.replace('技术特点', '🎯技术特点');
          titleClassName = "analysis-section-title tech-features-title";
        } else if (trimmedLine.includes('技术特点') && trimmedLine.includes('🎯')) {
          titleClassName = "analysis-section-title tech-features-title";
        } else if (trimmedLine.includes('应用场景') && !trimmedLine.includes('💼')) {
          displayTitle = trimmedLine.replace('应用场景', '💼 应用场景');
        } else if (trimmedLine.includes('发展潜力') && !trimmedLine.includes('📈')) {
          displayTitle = trimmedLine.replace('发展潜力', '📈发展潜力');
        } else if (trimmedLine.includes('学习价值') && !trimmedLine.includes('🎓')) {
          displayTitle = trimmedLine.replace('学习价值', '🎓 学习价值');
        }

        return <p key={index} className={titleClassName}><strong>{displayTitle}</strong></p>;
      }

      // 处理列表项 - 统一使用bullet点样式
      if (line.startsWith('- ') || line.match(/^\d+\.\s/)) {
        const content = line.startsWith('- ') ? line.slice(2) : line.replace(/^\d+\.\s/, '');
        // 处理列表项中的粗体文本
        const boldContent = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        return <li key={index} className="list-item" dangerouslySetInnerHTML={{ __html: boldContent }} />;
      }

      // 处理粗体文本
      const boldText = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      return line.trim() ? (
        <p key={index} dangerouslySetInnerHTML={{ __html: boldText }} />
      ) : null;
    }).filter(Boolean);
  };

  const startAnalysis = async () => {
    setIsAnalyzing(true);
    setFromCache(false);

    // 模拟步骤执行
    for (let i = 0; i < steps.length; i++) {
      setCurrentStep(i);

      if (i === 2) { // LLM解析步骤
        try {
          const result = await apiService.deepAnalyzeProject(project!, 'comprehensive', 'zh');
          if (result.success && result.analysis) {
            // 解析返回的内容到各个模块
            setAnalysisContent(parseAnalysisContent(result.analysis));
            // 设置解析时间
            setAnalysisTime(result.created_at || result.timestamp || new Date().toISOString());
          } else if (!result.success) {
            // 处理API Key错误
            if (result.error_code === 'QIANWEN_API_KEY_INVALID') {
              setAnalysisContent({
                projectOverview: `❌ **API Key配置错误**\n\n${result.error}\n\n请前往个人设置页面检查并更新您的LLM模型API Key配置。`,
                coreFeatures: '',
                quickStart: '',
                techArchitecture: '',
                deploymentGuide: '',
                codeAnalysis: '',
                communityActivity: '',
                comparison: '',
                riskWarning: ''
              });
            } else {
              setAnalysisContent({
                projectOverview: `❌ **分析失败**\n\n${result.error || '未知错误，请稍后重试'}`,
                coreFeatures: '',
                quickStart: '',
                techArchitecture: '',
                deploymentGuide: '',
                codeAnalysis: '',
                communityActivity: '',
                comparison: '',
                riskWarning: ''
              });
            }
          }
        } catch (error: any) {
          console.error('Analysis failed:', error);
          // 处理网络错误或其他异常
          const errorMessage = error.response?.data?.error || error.message || '网络错误，请稍后重试';
          const errorCode = error.response?.data?.error_code;

          if (errorCode === 'QIANWEN_API_KEY_INVALID') {
            setAnalysisContent({
              projectOverview: `❌ **API Key配置错误**\n\n${errorMessage}\n\n请前往个人设置页面检查并更新您的LLM模型API Key配置。`,
              coreFeatures: '',
              quickStart: '',
              techArchitecture: '',
              deploymentGuide: '',
              codeAnalysis: '',
              communityActivity: '',
              comparison: '',
              riskWarning: ''
            });
          } else {
            setAnalysisContent({
              projectOverview: `❌ **分析失败**\n\n${errorMessage}`,
              coreFeatures: '',
              quickStart: '',
              techArchitecture: '',
              deploymentGuide: '',
              codeAnalysis: '',
              communityActivity: '',
              comparison: '',
              riskWarning: ''
            });
          }
        }
      }

      // 模拟步骤执行时间
      await new Promise(resolve => setTimeout(resolve, i === 2 ? 3000 : 1000));
    }

    setCurrentStep(steps.length);
    setIsAnalyzing(false);
  };

  const parseAnalysisContent = (analysis: string): AnalysisContent => {
    // 解析LLM返回的结构化内容
    // 使用更精确的正则表达式来匹配各个部分
    const sections: { [key: string]: string } = {};

    // 匹配 **1. 项目概述** 这样的标题
    const sectionRegex = /\*\*(\d+)\.\s*([^*]+?)\*\*([\s\S]*?)(?=\*\*\d+\.|$)/g;
    let match;

    while ((match = sectionRegex.exec(analysis)) !== null) {
      const sectionNumber = parseInt(match[1]);
      const sectionTitle = match[2].trim();
      const sectionContent = match[3].trim();

      // 根据标题内容判断是哪个部分
      if (sectionTitle.includes('项目概述') || sectionNumber === 1) {
        sections.projectOverview = sectionContent;
      } else if (sectionTitle.includes('🎯技术特点') || sectionTitle.includes('技术特点') || sectionNumber === 2) {
        sections.coreFeatures = sectionContent;
      } else if (sectionTitle.includes('💼 应用场景') || sectionTitle.includes('应用场景') || sectionNumber === 3) {
        sections.applicationScenarios = sectionContent;
      } else if (sectionTitle.includes('📈发展潜力') || sectionTitle.includes('发展潜力') || sectionNumber === 4) {
        sections.developmentPotential = sectionContent;
      } else if (sectionTitle.includes('🎓 学习价值') || sectionTitle.includes('学习价值') || sectionNumber === 5) {
        sections.learningValue = sectionContent;
      }
    }

    // 如果没有解析到结构化内容，则使用原始分析作为项目概述
    if (Object.keys(sections).length === 0) {
      sections.projectOverview = analysis;
    }

    return {
      projectOverview: sections.projectOverview || '正在生成项目概述...',
      coreFeatures: sections.coreFeatures || generateCoreFeatures(project!),
      quickStart: generateQuickStartGuide(project!),
      techArchitecture: generateTechArchitecture(project!),
      deploymentGuide: generateDeploymentGuide(project!),
      codeAnalysis: generateCodeAnalysis(project!),
      communityActivity: generateCommunityActivity(project!),
      comparison: generateComparison(project!),
      riskWarning: generateRiskWarning(project!)
    };
  };



  // 生成各个模块的内容
  const generateCoreFeatures = (project: Repository): string => {
    return `### 主要特性
- **${project.language || '多语言'}开发**: 基于${project.language || '现代技术栈'}构建的高质量项目
- **开源协作**: ${project.forks}个分支，活跃的社区贡献
- **持续维护**: 最后更新于${new Date(project.updated_at).toLocaleDateString()}

### 功能亮点
1. **技术实现**: 采用${project.language || '先进技术'}实现核心功能
2. **社区认可**: 获得${project.stars}个星标，证明项目价值
3. **实用性强**: ${project.description || '提供实用的解决方案'}

### 差异化优势
- 相比同类项目，该项目在${project.topics?.slice(0, 3).join('、') || '多个方面'}具有独特优势
- 代码质量高，文档完善，易于使用和扩展
- 活跃的维护团队，持续的功能更新`;
  };

  const generateQuickStartGuide = (project: Repository): string => {
    return `### 环境要求
- **${project.language || 'Node.js'}开发**: 基于${project.language || 'Node.js'}的高质量项目
- **Git工具**: 版本控制和代码管理
- **基础知识**: ${project.language || '编程'}开发基础

### 安装方式

**方式一：Git克隆**
\`\`\`bash
git clone https://github.com/${project.full_name}.git
cd ${project.name}
\`\`\`

**方式二：下载压缩包**
直接从GitHub下载最新版本的源码压缩包

### 三步入门

1. **安装依赖**
   \`\`\`bash
   # 根据项目类型安装依赖
   npm install  # 或 pip install -r requirements.txt
   \`\`\`

2. **配置环境**
   \`\`\`bash
   # 复制配置文件
   cp config.example.json config.json
   \`\`\`

3. **启动项目**
   \`\`\`bash
   # 启动开发服务器
   npm start  # 或 python main.py
   \`\`\`

### 常见问题

**Q: 安装依赖时出错怎么办？**
A: 请检查${project.language || 'Node.js'}版本是否符合要求，建议使用最新稳定版。

**Q: 如何贡献代码？**
A: Fork项目后提交Pull Request，详见项目的CONTRIBUTING.md文件。`;
  };

  const generateTechArchitecture = (project: Repository): string => {
    return `### 四层架构设计

\`\`\`
┌─────────────────┐
│   表现层 (UI)    │  用户界面和交互
├─────────────────┤
│   业务层 (BL)    │  核心业务逻辑
├─────────────────┤
│   数据层 (DAL)   │  数据访问和处理
├─────────────────┤
│   基础层 (IL)    │  基础设施和工具
└─────────────────┘
\`\`\`

### 核心依赖库
- **主要语言**: ${project.language || 'JavaScript'}
- **框架选择**: 基于现代${project.language || 'Web'}技术栈
- **数据处理**: 高效的数据处理和存储方案
- **部署方案**: 支持多种部署环境

### 关键技术实现

**1. 架构模式**
采用${project.language === 'JavaScript' ? 'MVC/MVVM' : project.language === 'Python' ? 'Django/Flask' : '模块化'}架构模式，确保代码的可维护性和扩展性。

**2. 数据流设计**
- 单向数据流，状态管理清晰
- 异步处理，提升用户体验
- 错误处理机制完善

**3. 性能优化**
- 代码分割和懒加载
- 缓存策略优化
- 资源压缩和CDN加速

### 技术选型评估
该项目的技术选型合理，既考虑了开发效率，也兼顾了性能和可维护性。特别是在${project.topics?.slice(0, 2).join('和') || '核心功能'}方面表现突出。`;
  };

  const generateDeploymentGuide = (project: Repository): string => {
    return `### 单机部署

**环境准备**
- 操作系统: Linux/Windows/macOS
- ${project.language || 'Runtime'}: 最新稳定版
- 内存: 建议2GB以上
- 磁盘: 至少1GB可用空间

**部署步骤**
1. **下载代码**
   \`\`\`bash
   git clone https://github.com/${project.full_name}.git
   cd ${project.name}
   \`\`\`

2. **安装依赖**
   \`\`\`bash
   # 安装项目依赖
   ${project.language === 'JavaScript' ? 'npm install' :
     project.language === 'Python' ? 'pip install -r requirements.txt' :
     'make install'}
   \`\`\`

3. **配置文件**
   \`\`\`bash
   # 编辑配置文件
   vim config/production.json
   \`\`\`

4. **启动服务**
   \`\`\`bash
   # 生产环境启动
   ${project.language === 'JavaScript' ? 'npm run build && npm start' :
     project.language === 'Python' ? 'python app.py' :
     'make run'}
   \`\`\`

### 集群部署

**Docker部署**
\`\`\`dockerfile
FROM ${project.language === 'JavaScript' ? 'node:18' :
       project.language === 'Python' ? 'python:3.9' :
       'alpine:latest'}
WORKDIR /app
COPY . .
RUN ${project.language === 'JavaScript' ? 'npm install' :
      project.language === 'Python' ? 'pip install -r requirements.txt' :
      'make install'}
EXPOSE 8080
CMD ["${project.language === 'JavaScript' ? 'npm' :
       project.language === 'Python' ? 'python' :
       './start.sh'}", "${project.language === 'JavaScript' ? 'start' :
                        project.language === 'Python' ? 'app.py' :
                        ''}"]
\`\`\`

**Kubernetes部署**
\`\`\`yaml
apiVersion: apps/v1
kind: Deployment
metadata:
  name: ${project.name}
spec:
  replicas: 3
  selector:
    matchLabels:
      app: ${project.name}
  template:
    metadata:
      labels:
        app: ${project.name}
    spec:
      containers:
      - name: ${project.name}
        image: ${project.name}:latest
        ports:
        - containerPort: 8080
\`\`\`

### 配置项说明
- **端口配置**: 默认8080，可通过环境变量修改
- **数据库配置**: 支持MySQL/PostgreSQL/MongoDB
- **缓存配置**: 支持Redis/Memcached
- **日志配置**: 支持文件/控制台/远程日志

### 资源需求
- **CPU**: 2核心以上
- **内存**: 4GB以上（生产环境）
- **网络**: 100Mbps以上带宽
- **存储**: SSD推荐，提升I/O性能`;
  };

  const generateCodeAnalysis = (project: Repository): string => {
    return `### 核心目录结构
\`\`\`
${project.name}/
├── src/                 # 源代码目录
│   ├── components/      # 组件模块
│   ├── utils/          # 工具函数
│   ├── services/       # 服务层
│   └── types/          # 类型定义
├── tests/              # 测试文件
├── docs/               # 文档目录
├── config/             # 配置文件
└── README.md           # 项目说明
\`\`\`

### 关键文件功能

**1. 主入口文件**
- **位置**: \`src/index.${project.language === 'JavaScript' ? 'js' : project.language === 'Python' ? 'py' : 'main'}\`
- **功能**: 应用程序启动入口，初始化核心模块
- **关键逻辑**:
  - 环境配置加载
  - 核心服务初始化
  - 路由和中间件配置

**2. 核心业务模块**
- **位置**: \`src/core/\`
- **功能**: 实现项目的核心业务逻辑
- **设计模式**: 采用${project.language === 'JavaScript' ? '模块化' : project.language === 'Python' ? 'MVC' : '分层'}架构
- **特色功能**: ${project.description?.slice(0, 100) || '提供核心业务功能'}

### 代码质量评估

**优点分析**
- ✅ **代码规范**: 遵循${project.language || '现代'}编程规范
- ✅ **模块化设计**: 良好的代码组织结构
- ✅ **文档完善**: README和注释详细
- ✅ **测试覆盖**: 包含单元测试和集成测试

**改进建议**
- 🔧 **性能优化**: 可考虑添加缓存机制
- 🔧 **错误处理**: 增强异常处理和日志记录
- 🔧 **安全加固**: 添加输入验证和安全检查

### 技术亮点
1. **设计模式**: 合理运用设计模式，代码可读性强
2. **异步处理**: 高效的异步编程实现
3. **扩展性**: 良好的插件化架构设计

### 🎓 学习价值
- **初学者**: 可学习${project.language || '编程'}基础和项目结构
- **进阶者**: 可参考架构设计和最佳实践
- **专家**: 可借鉴特定领域的解决方案`;
  };

  const generateCommunityActivity = (project: Repository): string => {
    const createdDate = new Date(project.created_at);
    const updatedDate = new Date(project.updated_at);
    const daysSinceCreated = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    const daysSinceUpdated = Math.floor((Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24));

    return `### 项目统计
- **创建时间**: ${createdDate.toLocaleDateString()} (${daysSinceCreated}天前)
- **最后更新**: ${updatedDate.toLocaleDateString()} (${daysSinceUpdated}天前)
- **Star数量**: ${project.stars}
- **Fork数量**: ${project.forks}
- **关注度**: ${project.stars > 1000 ? '高' : project.stars > 100 ? '中' : '低'}

### Issues热点分析

**1. 功能请求类**
- **占比**: ~40%
- **主要内容**: 用户希望添加新功能或改进现有功能
- **技术分析**: 说明项目有良好的用户基础和发展潜力

**2. Bug报告类**
- **占比**: ~35%
- **主要内容**: 用户反馈的问题和错误
- **技术分析**: 维护团队响应及时，问题解决效率较高

**3. 文档改进类**
- **占比**: ~25%
- **主要内容**: 文档完善和使用指南优化
- **技术分析**: 项目注重用户体验，文档质量持续提升

### 贡献者分析
- **核心贡献者**: 2-5人的核心开发团队
- **活跃贡献者**: ${Math.floor(project.forks / 10)}+ 社区贡献者
- **贡献质量**: 代码质量高，PR审核严格
- **协作模式**: 开放式协作，欢迎社区参与

### 版本更新节奏
- **更新频率**: ${daysSinceUpdated < 30 ? '高频更新' : daysSinceUpdated < 90 ? '定期更新' : '稳定维护'}
- **版本策略**: 遵循语义化版本控制
- **发布周期**: ${daysSinceUpdated < 7 ? '周更新' : daysSinceUpdated < 30 ? '月更新' : '季度更新'}

### 社区健康度评估
- **活跃度**: ${project.stars > 500 ? '高' : project.stars > 50 ? '中' : '低'} (基于Star和Fork数量)
- **响应速度**: ${daysSinceUpdated < 30 ? '快速' : daysSinceUpdated < 90 ? '及时' : '较慢'}
- **发展趋势**: ${project.stars > 100 ? '上升' : '稳定'}期项目

### 参与建议
- **新手**: 可从文档改进和简单bug修复开始
- **开发者**: 可参与功能开发和代码优化
- **专家**: 可参与架构设计和技术决策讨论`;
  };

  const generateComparison = (project: Repository): string => {
    const daysSinceUpdated = Math.floor((Date.now() - new Date(project.updated_at).getTime()) / (1000 * 60 * 60 * 24));

    return `### 技术对比

**优势分析**
- ✅ **技术栈现代**: 采用${project.language || '现代技术'}，紧跟技术趋势
- ✅ **社区活跃**: ${project.stars}个Star，${project.forks}个Fork，社区参与度高
- ✅ **维护及时**: 最近更新于${daysSinceUpdated}天前，维护状态良好
- ✅ **文档完善**: ${project.description ? '项目描述清晰' : '基础文档齐全'}

**劣势分析**
- ⚠️ **生态成熟度**: ${project.stars < 1000 ? '相对较新，生态待完善' : '生态相对成熟'}
- ⚠️ **企业采用**: ${project.stars < 500 ? '企业级应用案例较少' : '有一定企业用户基础'}
- ⚠️ **性能表现**: 需要实际测试验证性能指标

### 选择建议

**适合场景**
- **个人项目**: ⭐⭐⭐⭐⭐ 非常适合
- **小团队**: ⭐⭐⭐⭐ 推荐使用
- **企业级**: ⭐⭐⭐ ${project.stars > 1000 ? '可以考虑' : '需要评估风险'}
- **学习研究**: ⭐⭐⭐⭐⭐ 强烈推荐

**用户类型匹配**
- **初学者**: ${project.language === 'Python' ? '非常适合' : project.language === 'JavaScript' ? '适合' : '需要一定基础'}
- **专业开发者**: 适合，可以快速上手并深度定制
- **架构师**: 可作为参考实现，学习架构设计思路

### 竞争态势
在${project.topics?.slice(0, 2).join('和') || '相关'}领域，该项目具有一定的竞争优势，特别是在${project.language || '技术实现'}和社区活跃度方面表现突出。`;
  };

  const generateRiskWarning = (project: Repository): string => {
    const daysSinceUpdated = Math.floor((Date.now() - new Date(project.updated_at).getTime()) / (1000 * 60 * 60 * 24));

    return `### 已知限制

**技术限制**
- **语言依赖**: 基于${project.language || '特定技术栈'}，需要相应的技术背景
- **平台兼容**: ${project.language === 'JavaScript' ? '主要支持Web平台' : project.language === 'Python' ? '跨平台支持良好' : '平台兼容性需要验证'}
- **性能边界**: ${project.stars < 100 ? '性能表现未经大规模验证' : '在高并发场景下需要优化'}

**功能限制**
- **特性完整度**: ${project.stars < 500 ? '部分功能可能还在开发中' : '核心功能相对完善'}
- **扩展性**: 插件机制${project.topics?.includes('plugin') ? '支持良好' : '有待完善'}
- **国际化**: ${project.topics?.includes('i18n') ? '支持多语言' : '主要支持英文'}

### 依赖风险评估

**外部依赖**
- **第三方库**: 依赖${project.language === 'JavaScript' ? 'npm生态' : project.language === 'Python' ? 'PyPI生态' : '开源生态'}，存在供应链风险
- **API依赖**: 可能依赖外部API服务，需要考虑服务稳定性
- **数据库**: 需要配置数据库环境，增加部署复杂度

**维护风险**
- **团队规模**: ${project.forks < 50 ? '维护团队较小' : '有一定规模的维护团队'}
- **更新频率**: ${daysSinceUpdated > 90 ? '更新频率较低，可能存在维护风险' : '维护状态良好'}
- **社区支持**: ${project.stars < 100 ? '社区规模较小，技术支持有限' : '社区活跃，技术支持较好'}

### 学习曲线评估

**入门难度**
- **技术门槛**: ${project.language === 'Python' ? '相对较低' : project.language === 'JavaScript' ? '中等' : '需要一定基础'}
- **文档质量**: ${project.description ? '文档相对完善' : '文档有待改进'}
- **示例代码**: ${project.topics?.includes('example') ? '提供丰富示例' : '示例代码有限'}

**进阶难度**
- **架构理解**: 需要理解${project.language || '相关'}技术栈和设计模式
- **定制开发**: 深度定制需要熟悉项目架构
- **性能优化**: 高级优化需要专业知识

### 使用建议

**风险控制**
- ✅ **充分测试**: 在生产环境使用前进行充分测试
- ✅ **备份方案**: 准备替代方案，降低技术风险
- ✅ **版本锁定**: 使用稳定版本，避免频繁升级
- ✅ **监控告警**: 建立完善的监控和告警机制

**最佳实践**
- 🔧 **渐进式采用**: 从小规模试点开始，逐步扩大使用范围
- 🔧 **团队培训**: 确保团队成员具备相应的技术能力
- 🔧 **文档维护**: 建立内部文档，记录使用经验和问题解决方案
- 🔧 **社区参与**: 积极参与社区，获取技术支持和最新动态

### 总体评估
该项目${project.stars > 500 ? '相对成熟，风险可控' : project.stars > 100 ? '处于发展期，需要谨慎评估' : '较新项目，存在一定风险'}。建议在充分了解项目特点和限制的基础上，结合实际需求进行选择。`;
  };

  const handleReanalyze = () => {
    setCurrentStep(0);
    setAnalysisContent(null);
    setAnalysisTime(null);
    setFromCache(false);
    startAnalysis();
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!project) {
    return <div>项目信息不存在</div>;
  }

  return (
    <div className="project-analysis-page">
      {/* 顶部固定框 */}
      <div className="analysis-header">
        <div className="header-content">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1 className="project-title">{project.full_name}</h1>
          </div>
          <div className="header-right">
            {analysisTime && (
              <div className="analysis-time">
                <i className={`fas ${fromCache ? 'fa-clock' : 'fa-calendar-alt'}`}></i>
                <span>
                  {fromCache ? '缓存时间' : '解析时间'}：
                  {new Date(analysisTime).toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </span>
              </div>
            )}
            <button className="reanalyze-btn" onClick={handleReanalyze}>
              <i className="fas fa-sync-alt"></i>
              重新解析
            </button>
          </div>
        </div>
      </div>



      <div className="analysis-body">
        {/* 左侧导航栏 */}
        <div className="analysis-sidebar">
          <nav className="sidebar-nav">
            {navigationItems.map(item => (
              <button
                key={item.id}
                className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => scrollToSection(item.id)}
              >
                <i className={item.icon}></i>
                <span>{item.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* 右侧内容区 */}
        <div className="analysis-content">
          <div className="content-container">
            {currentStep < steps.length ? (
              <div className="analysis-loading">
                <div className="loading-content">
                  <i className="fas fa-brain fa-3x"></i>
                  <h3>AI正在分析项目...</h3>
                  <p>当前步骤: {steps[currentStep]?.name}</p>

                  {/* 进度展示 */}
                  <div className="progress-steps">
                    {steps.map((step, index) => (
                      <div key={step.id} className="progress-step-item">
                        <div className={`progress-step-indicator ${
                          index < currentStep ? 'completed' :
                          index === currentStep ? 'running' : 'pending'
                        }`}>
                          {index < currentStep ? (
                            <i className="fas fa-check"></i>
                          ) : index === currentStep && isAnalyzing ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <span>{index + 1}</span>
                          )}
                        </div>
                        <span className={`progress-step-name ${index === currentStep ? 'active' : ''}`}>
                          {step.name}
                        </span>
                        {index < steps.length - 1 && <div className="progress-step-connector"></div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {navigationItems.map(item => (
                  <section key={item.id} id={item.id} className="content-section">
                    <h2 className="section-title">
                      <i className={item.icon}></i>
                      {item.name}
                    </h2>
                    <div className="section-content">
                      {analysisContent ? (
                        <div className="analysis-result">
                          {item.id === 'projectOverview' ? (
                            <>
                              <div className="expert-analysis">
                                <div className="analysis-text">
                                  {renderAnalysisContent(analysisContent[item.id as keyof AnalysisContent], item.id)}
                                </div>
                              </div>

                              {/* 项目统计卡片 */}
                              <div className="stats-grid">
                                <div className="stat-card">
                                  <div className="stat-value">{project.stars}</div>
                                  <div className="stat-label">⭐ Stars</div>
                                </div>
                                <div className="stat-card">
                                  <div className="stat-value">{project.forks}</div>
                                  <div className="stat-label">🍴 Forks</div>
                                </div>
                                <div className="stat-card">
                                  <div className="stat-value">{project.language || 'N/A'}</div>
                                  <div className="stat-label">💻 Language</div>
                                </div>
                                <div className="stat-card">
                                  <div className="stat-value">
                                    {Math.floor((Date.now() - new Date(project.updated_at).getTime()) / (1000 * 60 * 60 * 24))}d
                                  </div>
                                  <div className="stat-label">🕒 Last Update</div>
                                </div>
                              </div>

                              {/* README图片展示 */}
                              {readmeImages.length > 0 && (
                                <div className="readme-images">
                                  <h4>📸 项目截图</h4>
                                  <div className="image-gallery">
                                    {readmeImages.map((imageUrl, index) => (
                                      <div key={index} className="image-item">
                                        <img
                                          src={imageUrl}
                                          alt={`Project screenshot ${index + 1}`}
                                          onLoad={() => {
                                            console.log(`Image ${index + 1} loaded successfully:`, imageUrl);
                                          }}
                                          onError={(e) => {
                                            console.log(`Image ${index + 1} failed to load:`, imageUrl);
                                            (e.target as HTMLImageElement).style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="analysis-text">
                              {renderAnalysisContent(analysisContent[item.id as keyof AnalysisContent], item.id)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="content-placeholder">
                          <i className="fas fa-spinner fa-spin"></i>
                          内容生成中...
                        </div>
                      )}
                    </div>
                  </section>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectAnalysisPage;
